import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/integrations/db/client";

/**
 * Drop every cached query when the signed-in person changes.  tickets[7533]
 *
 * The cache is per tab, not per person, and much of it is staleTime: Infinity
 * (departments, rosters, access lists). So logging out and logging in as
 * somebody else in the same tab — no reload — rendered the FIRST person's rows
 * until something happened to refetch them. The database was right the whole
 * time; the screen was not, and for a department-restricted or private-ticket
 * viewer that screen is exactly what they are not allowed to see.
 *
 * Keyed on the user id rather than on event names, so it covers a sign-out
 * (id → none), a login over a session that never signed out cleanly (A → B),
 * and ignores TOKEN_REFRESHED / USER_UPDATED for the same person. The first
 * event only records who is signed in — clearing on page load would throw away
 * nothing but the work of the first render.
 *
 * Switching workspace is not handled here: same person, and useSwitchTenant
 * already invalidates everything itself.
 */
export const useSessionCacheReset = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    // undefined = not yet known; null = known to be signed out.
    let lastUserId: string | null | undefined;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const userId = session?.user?.id ?? null;
      if (lastUserId !== undefined && userId !== lastUserId) queryClient.clear();
      lastUserId = userId;
    });

    return () => subscription.unsubscribe();
  }, [queryClient]);
};
